import type { ReactNode } from 'react';
import type { MorrisonsPayload, MorrisonsWeek } from '../report';
import { fmtDay, fmtDec, fmtInt, fmtPct, ratio, share } from '../format';
import { CardHeatmap, DailyStickersChart, PrizeDonut, WeeklyBars, type DonutPart } from './Charts';
import { rarestCards, stickersTotal, weekStickers } from '../derive';

/*
 * The dashboard's cards in the design's order. Each takes the payload and
 * derives its rates; a figure the source cannot give reads "—".
 */

type P = { data: MorrisonsPayload };

const Tile = ({ label, value, sub }: { label: string; value: string; sub?: ReactNode }) => (
  <div className="mo-tile">
    <span className="mo-tile-l">{label}</span>
    <span className="mo-tile-v">{value}</span>
    {sub && <span className="mo-tile-s">{sub}</span>}
  </div>
);

const Stat = ({ label, value, green, sub }: { label: string; value: string; green?: boolean; sub?: ReactNode }) => (
  <div>
    <div className="mo-stat-l">{label}</div>
    <div className={`mo-stat-v${green ? ' g' : ''}`}>{value}</div>
    {sub && <div className="mo-tile-s">{sub}</div>}
  </div>
);

/** A thin bar with its value as a share (0–100). */
const Track = ({ pct }: { pct: number | null }) => (
  <div className="mo-track">{pct != null && <div className="mo-fill" style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} />}</div>
);

/** The one-line take-away under the header. */
export function TakeAway({ data }: P) {
  const t = data.totals;
  return (
    <p className="mo-take">
      <b>{fmtInt(stickersTotal(t) ?? t.newCards)}</b> stickers scanned · <b>{fmtInt(t.optIns)}</b> opt-ins · <b>{fmtPct(share(t.collectors, t.optIns), 0)}</b>{' '}
      of opt-ins collecting
    </p>
  );
}

export function StickersCard({ data }: P) {
  const t = data.totals;
  const total = stickersTotal(t);
  const live = data.weeks.find((w) => w.status === 'live');
  return (
    <section className="mo-card" id="stickers">
      <div className="mo-head">
        <h2 className="mo-h">Stickers scanned — whole campaign</h2>
        {live && <span className="mo-tag">Week {live.week} in progress</span>}
      </div>
      <div className="mo-hero">
        <span className="mo-big">{fmtInt(total ?? t.newCards)}</span>
        <span className="mo-note">
          {total == null
            ? 'new cards added by camera scan or manual entry since launch (duplicate scans need the app events)'
            : 'stickers added by camera scan or manual entry since launch'}
        </span>
      </div>
      <div className="mo-head">
        <h3 className="mo-sub">Stickers scanned per day</h3>
        <div className="mo-legend">
          <span>
            <i className="mo-sw new" /> Scans
          </span>
          <span>
            <i className="mo-sw hot" /> Today (partial)
          </span>
        </div>
      </div>
      <DailyStickersChart days={data.days} />
      <p className="mo-note">All stickers scanned per day, camera and manual entry. Dashed lines mark the start of each week (Monday).</p>
    </section>
  );
}

export function ReachTiles({ data }: P) {
  const t = data.totals;
  return (
    <div className="mo-tiles" id="reach">
      <Tile
        label="Unique visitors"
        value={fmtInt(t.uniqueVisitors)}
        sub={
          <>
            mobile, whole campaign · <b>{fmtInt(t.unsupportedDeviceViews)}</b> unsupported-device views
          </>
        }
      />
      <Tile
        label="Opt-ins"
        value={fmtInt(t.optIns)}
        sub={
          <>
            <b>{fmtPct(share(t.optIns, t.uniqueVisitors))}</b> of unique visitors
          </>
        }
      />
      <Tile
        label="Unique players"
        value={fmtInt(t.uniquePlayers)}
        sub={
          <>
            <b>{fmtPct(share(t.uniquePlayers, t.optIns))}</b> of opt-ins played a game
          </>
        }
      />
      <Tile label="Daily active users" value={fmtInt(t.dailyActiveAvg)} sub="average per day, members opening a flowpack" />
      <Tile
        label="Return rate"
        value={fmtPct(share(t.returningUsers, t.activeUsers))}
        sub={
          <>
            active members back on more than one day · <b>{fmtDec(ratio(t.activeDays, t.activeUsers))}</b> active days on average
          </>
        }
      />
    </div>
  );
}

export function ScanMethodCard({ data }: P) {
  const t = data.totals;
  // Every scan by method once the app events are in (duplicates included); new cards until then.
  const all = t.cameraScans != null && t.manualScans != null;
  const camera = t.cameraScans ?? t.cameraCards;
  const manual = t.manualScans ?? t.manualCards;
  const manualPct = share(manual, camera + manual);
  return (
    <section className="mo-card" id="scan-method">
      <h3 className="mo-h">Scan method</h3>
      <div className="mo-track tall" role="img" aria-label={`Camera ${fmtPct(share(camera, camera + manual))}, manual entry ${fmtPct(manualPct)}`}>
        {manualPct != null && (
          <>
            <div className="mo-fill sq" style={{ width: `${100 - manualPct}%` }} />
            <div className="mo-fill y sq" style={{ width: `${manualPct}%` }} />
          </>
        )}
      </div>
      <div className="mo-legend">
        <span>
          <i className="mo-sw new" /> Camera <b>{fmtInt(camera)}</b>
        </span>
        <span>
          <i className="mo-sw y" /> Manual entry <b>{fmtInt(manual)}</b>
        </span>
      </div>
      <div className="mo-stats">
        <Stat label={`Users hitting the ${data.campaign.manualEntriesPerDay}/day manual limit`} value={fmtPct(share(t.manualLimitUsers, t.collectors))} />
      </div>
      {!all && <p className="mo-note">New cards only — duplicate scans need the app events.</p>}
    </section>
  );
}

export function MilestonesCard({ data }: P) {
  const t = data.totals;
  const steps = t.milestones;
  const labels: Record<number, string> = {
    1: 'At least 1 card',
    12: '12 cards · first flowpack',
    // Games open by date, one a week — not by cards collected (BE review, 2 Nov).
    30: '30 cards',
    60: '60 cards',
    90: '90 cards',
    120: '120 cards · collection complete',
  };
  // The step with the biggest relative drop from the one before is red.
  const drops = steps.map((s, i) => (i === 0 || steps[i - 1].users === 0 ? 0 : 1 - s.users / steps[i - 1].users));
  const worst = drops.indexOf(Math.max(...drops));
  return (
    <section className="mo-card">
      <h3 className="mo-h">Collection milestones</h3>
      <div className="mo-stats">
        <Stat label="Collecting users" value={fmtInt(t.collectors)} green />
        <Stat label={`Unique cards per collector (of ${data.campaign.collectionSize})`} value={fmtDec(ratio(t.newCards, t.collectors))} />
      </div>
      <div className="mo-funnel">
        {steps.map((s, i) => (
          <div key={s.cards} className={`mo-frow${i === worst && drops[i] > 0 ? ' is-max' : ''}`}>
            <div className="mo-frow-top">
              <span>{labels[s.cards] ?? `${s.cards} cards`}</span>
              <span>
                <b>{fmtInt(s.users)}</b>
              </span>
            </div>
            <Track pct={share(s.users, t.collectors)} />
          </div>
        ))}
      </div>
      <p className="mo-note">Members who reached each milestone. Bars are scaled to collecting users; the step with the biggest drop is shown in red.</p>
    </section>
  );
}

export function CardsCard({ data }: P) {
  const rare = rarestCards(data.cards);
  return (
    <section className="mo-card">
      <h3 className="mo-h">Scans per card, 001–{data.campaign.collectionSize}</h3>
      <div className="mo-ramp">
        fewer <i /> more <em /> 5 rarest
      </div>
      <CardHeatmap cards={data.cards} />
      <p className="mo-note">
        {rare.length > 0 ? (
          <>
            Rarest: <b>{rare.map((c) => `${c.card} (${fmtInt(c.collected)})`).join(', ')}</b> — cards that may be rare, missing in stores or hard to scan.
          </>
        ) : (
          'No card collected yet.'
        )}
      </p>
    </section>
  );
}

const SOURCE_LABELS: Array<[keyof MorrisonsPayload['totals']['flowpacksGranted'], string]> = [
  ['daily', 'Daily pack'],
  ['milestone12', '12-sticker milestone'],
  ['share', 'Social share'],
  ['referralInviter', 'Referral — inviter'],
  ['referralInvitee', 'Referral — invitee'],
  ['firstWin', 'First win per game'],
  ['welcome', 'Welcome (opt-in)'],
  ['other', 'Other'],
];

export function FlowpacksCard({ data }: P) {
  const t = data.totals;
  const g = t.flowpacksGranted;
  const granted = Object.values(g).reduce((a, b) => a + b, 0);
  const max = Math.max(1, ...Object.values(g));
  return (
    <section className="mo-card">
      <h3 className="mo-h">Flowpacks granted by source</h3>
      <div className="mo-pair">
        <div>
          <div className="mo-stat-l">Granted</div>
          <div className="mo-big sm">{fmtInt(granted)}</div>
        </div>
        <div>
          <div className="mo-stat-l">Opened</div>
          <div className="mo-big sm">{fmtInt(t.flowpacksOpened)}</div>
          <div className="mo-tile-s">
            <b>{fmtPct(share(t.flowpacksOpened, granted))}</b> open rate
          </div>
        </div>
      </div>
      <div className="mo-hbars">
        {SOURCE_LABELS.filter(([k]) => g[k] > 0 || !['welcome', 'other'].includes(k)).map(([k, label]) => (
          <div className="mo-hbar" key={k}>
            <span>{label}</span>
            <Track pct={(g[k] / max) * 100} />
            <span className="v">
              <b>{fmtInt(g[k])}</b>
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

export function PrizesCard({ data }: P) {
  const p = data.totals.prizes;
  const caps = data.campaign.prizeCaps;
  const parts: DonutPart[] = [
    { key: 'lottery', label: 'Lottery ticket', value: p.lottery, color: 'var(--mo-green)' },
    { key: 'points500', label: '500 points', value: p.points500, color: 'var(--mo-green-2)' },
    { key: 'points5000', label: '5,000 points', value: p.points5000, color: 'var(--mo-hot)' },
    { key: 'freeFruit', label: 'Free Disney Fruit', value: p.freeFruit, color: 'var(--mo-yellow)' },
    { key: 'freeBakingKit', label: 'Free Disney Baking Kit', value: p.freeBakingKit, color: 'var(--mo-green-3)' },
    ...(p.other > 0 ? [{ key: 'other', label: 'Other', value: p.other, color: 'var(--mo-muted)' }] : []),
  ];
  const capOf = (key: string) => {
    if (!(key in caps)) return null;
    const cap = caps[key as keyof typeof caps];
    return cap == null ? '· unlimited' : `of ${fmtInt(cap)} max`;
  };
  return (
    <section className="mo-card">
      <h3 className="mo-h">Prize outcomes of opened flowpacks</h3>
      <div className="mo-prize">
        <PrizeDonut parts={parts} />
        <div className="mo-plegend">
          {parts.map((x) => (
            <div key={x.key}>
              <span>
                <i className="mo-sw" style={{ background: x.color }} /> {x.label}
              </span>
              <span className="mo-pv">
                <b>{fmtInt(x.value)}</b> {capOf(x.key) && <span className="mo-cap">{capOf(x.key)}</span>}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function GamesCard({ data }: P) {
  const t = data.totals;
  const games = data.games;
  const plays = games.reduce((a, g) => a + g.plays, 0);
  const winRate = share(t.gameWinners, t.uniquePlayers);
  return (
    <section className="mo-card" id="games">
      <div className="mo-stats">
        <Stat label="Game plays" value={fmtInt(plays)} green />
        <Stat
          label="Overall win rate"
          value={fmtPct(winRate)}
          sub={
            winRate != null && (
              <>
                <b>{fmtInt(t.gameWinners)}</b> of {fmtInt(t.uniquePlayers)} players won at least once
              </>
            )
          }
        />
      </div>
      <div className="mo-tablebox flat">
        <table aria-label="Games">
          <thead>
            <tr>
              <th>Game</th>
              <th>Plays</th>
              <th>Wins</th>
              <th>Time’s up</th>
              <th>Quits</th>
              <th>Win rate</th>
            </tr>
          </thead>
          <tbody>
            {games.map((g) => (
              <tr key={g.id}>
                <td>
                  {g.position}. {g.name}
                </td>
                <td>{fmtInt(g.plays)}</td>
                <td>{fmtInt(g.wins)}</td>
                <td>{fmtInt(g.timeUps)}</td>
                <td>{fmtInt(g.quits)}</td>
                <td>
                  <span className="cellbar">
                    <span style={{ width: `${share(g.winners, g.players) ?? 0}%` }} />
                  </span>
                  {fmtPct(share(g.winners, g.players))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mo-note">Win rate is the share of players who won at least once — of everyone who played, for each game and overall.</p>
    </section>
  );
}

export function MissionsCard({ data }: P) {
  const t = data.totals;
  const r = t.referral;
  return (
    <section className="mo-card" id="missions">
      <div className="mo-mgrid">
        <div className="mo-mcol" id="referral">
          <h3 className="mo-h">Invite a friend</h3>
          <div className="mo-stats">
            <Stat label="Codes shared" value={fmtInt(r.codesShared)} />
            <Stat label="Codes redeemed" value={fmtInt(r.redeemed)} green />
            <Stat label="Invitee opt-ins" value={fmtInt(r.inviteeOptIns)} />
            <Stat
              label={`Inviters at the ${data.campaign.referralCap}-invite max`}
              value={fmtPct(share(r.invitersAtMax, r.inviters))}
              sub={
                <>
                  of <b>{fmtInt(r.inviters)}</b> inviters
                </>
              }
            />
          </div>
          <p className="mo-note">The inviter gets +1 flowpack per redeemed code (max {data.campaign.referralCap}); the invited friend also gets 1 flowpack.</p>
        </div>
        <div className="mo-mcol" id="share">
          <h3 className="mo-h">Share on social media</h3>
          <div className="mo-stats">
            <Stat label="Shares completed" value={fmtInt(t.shares)} />
          </div>
          <div className="mo-stats">
            <Stat label="Opted-in users who shared" value={fmtPct(share(t.sharers, t.optIns))} sub="completed the share mission" />
          </div>
          <p className="mo-note">One-time mission, +1 flowpack.</p>
        </div>
      </div>
    </section>
  );
}

export function WeeklyCard({ data }: P) {
  return (
    <section className="mo-card">
      <h3 className="mo-h">Stickers scanned per week</h3>
      <WeeklyBars weeks={data.weeks} />
      <p className="mo-note">Weeks run Monday to Sunday; the current week is in red.</p>
    </section>
  );
}

const WEEK_ROWS: Array<[string, (w: MorrisonsWeek) => number | null]> = [
  ['Visitors', (w) => w.visitors],
  ['Opt-ins', (w) => w.optIns],
  ['Active users', (w) => w.activeUsers],
  ['Stickers scanned', weekStickers],
  ['Flowpacks granted', (w) => w.flowpacksGranted],
  ['Flowpacks opened', (w) => w.flowpacksOpened],
  ['Lottery tickets', (w) => w.lotteryTickets],
  ['Game plays', (w) => w.gamePlays],
  ['Referrals', (w) => w.referrals],
  ['Shares', (w) => w.shares],
];

/** One row per metric, one column per campaign week; weeks still to come read "—". */
export function WeeklyTable({ data }: P) {
  const tone = (w: MorrisonsWeek) => (w.status === 'live' ? 'cur' : w.status === 'planned' ? 'future' : undefined);
  return (
    <section className="mo-card" id="summary">
      <div className="mo-tablebox flat mo-wk">
        <table aria-label="Weekly summary">
          <thead>
            <tr>
              <th scope="col">Metric</th>
              {data.weeks.map((w) => (
                <th key={w.week} scope="col" className={tone(w)}>
                  Week {w.week}
                  <span className="mo-wk-sub">
                    {w.start === w.end ? fmtDay(w.start, 'dayMonth') : `${fmtDay(w.start, 'dayMonth')} – ${fmtDay(w.end, 'dayMonth')}`}
                    {w.status === 'live' ? ' · in progress' : ''}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {WEEK_ROWS.map(([label, value]) => (
              <tr key={label}>
                <th scope="row">{label}</th>
                {data.weeks.map((w) => (
                  <td key={w.week} className={tone(w)}>
                    {fmtInt(value(w))}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mo-note">Visitors and active users are counted per week, so their sum is higher than the campaign’s unique totals.</p>
    </section>
  );
}
